// Tela "Envio ao Carefy": arrasta o PDF da evolução do IW, a tela mostra o formulário do Carefy já
// preenchido (à esquerda) e de onde saiu cada resposta no relatório do IW (à direita).
// Campos em amarelo: o IW não tem — preencher antes de enviar (o Carefy exige todos).
// "Imputar agora" grava o formulário no Carefy com o login configurado no servidor (autorizado pelo
// Alexandre em 15/10/2026): só liga com todos os obrigatórios preenchidos e o paciente achado no Carefy.
import React, { useEffect, useRef, useState } from 'react';
import { Upload, Copy, Send, FileText } from 'lucide-react';
import { supabase } from '../supabase';
import PendenciasCarefy from './PendenciasCarefy';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#4f46e5' };
const RELATORIOS = [
  { k: 'enfermagem', l: 'Relatório de Enfermagem', ok: true },
  { k: 'medico', l: 'Relatório Médico', ok: true },
  { k: 'fisioterapia', l: 'Relatório Fisioterapêutico', ok: true },
  { k: 'fonoaudiologia', l: 'Relatório Fonoaudiológico', ok: true },
  { k: 'terapia_ocupacional', l: 'Terapia Ocupacional / Psicomotricidade', ok: true },
  { k: 'nutricao', l: 'Relatório Nutricional (avaliação ou prorrogação, pediátrica ou adulto)', ok: true },
  { k: 'psicologia', l: 'Relatório de Psicologia', ok: true },
];
const ORIGEM = {
  iw: { fundo: '#dcfce7', cor: '#166534', texto: 'do IW' },
  sugerido: { fundo: '#ffedd5', cor: '#9a3412', texto: 'sugerido — conferir' },
  falta: { fundo: '#fef9c3', cor: '#854d0e', texto: 'o IW não tem — preencher' },
};
const fmtData = iso => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const paraBase64 = arquivo => new Promise((ok, erro) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).split(',')[1]);
  r.onerror = () => erro(new Error('Não foi possível ler o arquivo.'));
  r.readAsDataURL(arquivo);
});

export default function EnvioCarefy() {
  const [aba, setAba] = useState('pacientes');
  const [relatorio, setRelatorio] = useState('enfermagem');
  const [arquivo, setArquivo] = useState(null);
  const [dados, setDados] = useState(null);
  const [valores, setValores] = useState({});
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [evolucao, setEvolucao] = useState(null); // evolução da tabela do IW sendo validada (sem PDF)
  const entrada = useRef(null);

  const chamar = async corpo => {
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch('/api/carefy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
      body: JSON.stringify(corpo),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
    return j;
  };
  const carregarHistorico = () => chamar({ acao: 'historico' }).then(j => setHistorico(j.envios || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarHistorico(); }, []);

  const analisar = async f => {
    if (!f) return;
    if (!/pdf$/i.test(f.type) && !/\.pdf$/i.test(f.name)) { setErro('Envie o PDF da evolução exportado do IW.'); return; }
    setArquivo(f); setEvolucao(null); setErro(''); setDados(null); setEnviado(null); setCarregando(true);
    try {
      const j = await chamar({ acao: 'analisar', formulario: relatorio, pdf: await paraBase64(f) });
      setDados(j);
      setValores(Object.fromEntries(j.campos.map(c => [c.id, c.valor || ''])));
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  };

  // "Validar" na lista: abre o formulário preenchido a partir da evolução da tabela do IW (22/10)
  const validar = async (rel, evolucaoId) => {
    setRelatorio(rel); setAba('enviar'); setArquivo(null); setEvolucao(evolucaoId); setErro(''); setDados(null); setEnviado(null); setCarregando(true);
    try {
      const j = await chamar({ acao: 'analisar', formulario: rel, evolucaoId });
      setDados(j);
      setValores(Object.fromEntries(j.campos.map(c => [c.id, c.valor || ''])));
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  };

  const campos = dados?.campos || [];
  const vazios = campos.filter(c => c.obrigatorio && !String(valores[c.id] || '').trim());
  const conta = o => campos.filter(c => c.origem === o).length;
  const podeEnviar = dados && !dados.carefy.erro && !vazios.length && !enviando && !enviado;
  const imputar = async () => {
    if (!podeEnviar) return;
    if (!window.confirm(`Enviar o ${dados.formulario} de ${dados.carefy.paciente.nome} ao Carefy agora?

O formulário fica gravado no sistema da Unimed com o login configurado.`)) return;
    setEnviando(true); setErro('');
    try {
      const j = await chamar({ acao: 'enviar', formulario: relatorio, paciente: dados.pacienteIW, respostas: valores, arquivo: evolucao ? `tabela IW — evolução ${evolucao}` : arquivo?.name || '' });
      setEnviado(j);
    } catch (e) { setErro(`Não enviado: ${e.message}`); }
    setEnviando(false);
    carregarHistorico();
  };
  const copiar = async () => {
    const txt = campos.map(c => `${c.titulo}\n${c.tipo === 'data-hora' ? String(valores[c.id] || '').replace('T', ' ') : valores[c.id] || ''}`).join('\n\n');
    try { await navigator.clipboard.writeText(txt); setCopiado(true); setTimeout(() => setCopiado(false), 2500); } catch (_) { setErro('O navegador não deixou copiar.'); }
  };

  const caixa = { background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, padding: 16 };
  const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
  const inp = { width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', fontSize: '0.85rem', fontFamily: 'inherit', color: P.text, background: P.white };

  return (
    <div style={{ padding: '24px 28px', background: P.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: P.text, display: 'flex', alignItems: 'center', gap: 8 }}><Send size={22} /> Envio ao Carefy</h1>
        <div style={{ color: P.muted, fontSize: '0.85rem', marginTop: 4 }}>
          Relatórios dos pacientes Saúde Unimed (Unimed Brasil) no Carefy: o que cada paciente precisa enviar no mês e o envio a partir da evolução do IW.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, background: '#e2e8f0', borderRadius: 10, padding: 3, width: 'fit-content' }}>
        {[['pacientes', '📋 Pacientes Unimed Brasil'], ['enviar', '📤 Enviar relatório']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setAba(k)} style={{ border: 0, borderRadius: 8, padding: '7px 18px', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', background: aba === k ? P.white : 'transparent', color: aba === k ? P.primary : P.muted, boxShadow: aba === k ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }}>{l}</button>
        ))}
      </div>

      {aba === 'pacientes' && <PendenciasCarefy onEnviar={rel => { setRelatorio(rel); setEvolucao(null); setDados(null); setAba('enviar'); }} onValidar={validar} />}

      {aba === 'enviar' && <>
      <div style={{ ...caixa, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: P.muted, marginBottom: 6 }}>Relatório</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {RELATORIOS.map(r => (
              <label key={r.k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.85rem', color: r.ok ? P.text : '#94a3b8', cursor: r.ok ? 'pointer' : 'not-allowed' }}>
                <input type="radio" name="relatorio" disabled={!r.ok} checked={relatorio === r.k} onChange={() => { setRelatorio(r.k); setDados(null); }} />
                {r.l}{!r.ok && <span style={{ fontSize: '0.7rem' }}>(em breve)</span>}
              </label>
            ))}
          </div>
        </div>
        <div
          onDragOver={e => { e.preventDefault(); setArrastando(true); }}
          onDragLeave={() => setArrastando(false)}
          onDrop={e => { e.preventDefault(); setArrastando(false); analisar(e.dataTransfer.files[0]); }}
          onClick={() => entrada.current?.click()}
          role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') entrada.current?.click(); }}
          style={{ flex: 1, minWidth: 260, border: `2px dashed ${arrastando ? P.primary : '#cbd5e1'}`, background: arrastando ? '#eef2ff' : P.bg, borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', textAlign: 'center' }}>
          <Upload size={22} color={P.primary} />
          <div style={{ fontWeight: 700, color: P.text, fontSize: '0.9rem' }}>{carregando ? 'Lendo o relatório e conferindo no Carefy…' : 'Arraste aqui o PDF da evolução do IW ou clique para escolher'}</div>
          {arquivo && <div style={{ fontSize: '0.75rem', color: P.muted, display: 'flex', gap: 4, alignItems: 'center' }}><FileText size={13} /> {arquivo.name}</div>}
          {evolucao && <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 700 }}>{carregando ? 'Montando a partir da tabela do IW…' : `📄 Evolução ${evolucao} da tabela do IW (sem PDF) — confira e ajuste antes de enviar`}</div>}
          <input ref={entrada} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={e => { analisar(e.target.files[0]); e.target.value = ''; }} />
        </div>
      </div>

      {erro && <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px', fontSize: '0.85rem' }}>{erro}</div>}

      {dados && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ ...caixa, flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: P.muted }}>PACIENTE NO RELATÓRIO DO IW</div>
              <div style={{ fontWeight: 800, color: P.text, marginTop: 2 }}>{dados.pacienteIW.nome || '—'}</div>
              <div style={{ fontSize: '0.8rem', color: P.muted }}>Nascimento: {fmtData(dados.pacienteIW.nascimento)}</div>
            </div>
            <div style={{ ...caixa, flex: 1, minWidth: 260, borderColor: dados.carefy.erro ? '#fecaca' : '#bbf7d0' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: P.muted }}>NO CAREFY</div>
              {dados.carefy.erro
                ? <div style={{ color: '#991b1b', fontWeight: 700, marginTop: 2, fontSize: '0.85rem' }}>✕ {dados.carefy.erro}</div>
                : <>
                    <div style={{ color: '#166534', fontWeight: 800, marginTop: 2 }}>✓ {dados.carefy.paciente.nome}</div>
                    <div style={{ fontSize: '0.8rem', color: P.muted }}>
                      Internação aberta desde {fmtData(dados.carefy.internacao.desde)} · {dados.carefy.internacao.tipo}
                      <div>{dados.carefy.internacao.prestador}</div>
                    </div>
                  </>}
            </div>
            <div style={{ ...caixa, minWidth: 220 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: P.muted }}>{dados.formulario}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                <span style={chip(ORIGEM.iw.fundo, ORIGEM.iw.cor)}>{conta('iw')} do IW</span>
                <span style={chip(ORIGEM.sugerido.fundo, ORIGEM.sugerido.cor)}>{conta('sugerido')} sugeridos — conferir</span>
                <span style={chip(ORIGEM.falta.fundo, ORIGEM.falta.cor)}>{conta('falta')} o IW não tem — preencher</span>
              </div>
            </div>
          </div>

          <div style={{ ...caixa, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', background: P.bg, borderBottom: `1px solid ${P.border}`, fontSize: '0.72rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '.04em' }}>
              <div style={{ padding: '10px 14px' }}>Formulário do Carefy</div>
              <div style={{ padding: '10px 14px', borderLeft: `1px solid ${P.border}` }}>De onde veio ({dados.fonte === 'tabela' ? 'tabela de evoluções do IW' : 'relatório do IW'})</div>
            </div>
            {campos.map(c => {
              const o = ORIGEM[c.origem] || ORIGEM.falta;
              const vazio = !String(valores[c.id] || '').trim();
              const set = v => setValores(prev => ({ ...prev, [c.id]: v }));
              return (
                <div key={c.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', borderBottom: `1px solid #f1f5f9`, background: vazio && c.obrigatorio ? '#fefce8' : undefined }}>
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                      <label htmlFor={`c-${c.id}`} style={{ fontSize: '0.8rem', fontWeight: 700, color: P.text }}>{c.titulo}{c.obrigatorio && <span style={{ color: '#dc2626' }}> *</span>}</label>
                      <span style={chip(o.fundo, o.cor)}>{o.texto}</span>
                    </div>
                    {c.tipo === 'opcao' ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {c.opcoes.map(op => (
                          <label key={op} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: '0.82rem', border: `1px solid ${valores[c.id] === op ? P.primary : P.border}`, background: valores[c.id] === op ? '#eef2ff' : P.white, borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>
                            <input type="radio" name={`c-${c.id}`} checked={valores[c.id] === op} onChange={() => set(op)} /> {op}
                          </label>
                        ))}
                      </div>
                    ) : c.tipo === 'data-hora' ? (
                      <input id={`c-${c.id}`} type="datetime-local" value={valores[c.id] || ''} onChange={e => set(e.target.value)} style={{ ...inp, maxWidth: 240 }} />
                    ) : (
                      <textarea id={`c-${c.id}`} value={valores[c.id] || ''} onChange={e => set(e.target.value)}
                        rows={Math.min(14, Math.max(2, String(valores[c.id] || '').split('\n').length + 1))} style={{ ...inp, resize: 'vertical' }} />
                    )}
                  </div>
                  <div style={{ padding: '12px 14px', borderLeft: `1px solid ${P.border}`, fontSize: '0.75rem', color: '#475569', whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>
                    {c.trecho || <span style={{ color: '#94a3b8' }}>—</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ ...caixa, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', position: 'sticky', bottom: 12, boxShadow: '0 4px 16px rgba(15,23,42,.08)' }}>
            <div style={{ fontSize: '0.82rem', color: vazios.length ? '#854d0e' : '#166534', fontWeight: 700, marginRight: 'auto' }}>
              {vazios.length ? `Faltam ${vazios.length} campo(s) obrigatório(s): ${vazios.map(c => c.titulo.split(' (')[0]).join(', ')}` : '✓ Todos os campos obrigatórios preenchidos'}
            </div>
            <button type="button" onClick={copiar} style={{ border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '9px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', color: P.text }}>
              <Copy size={15} /> {copiado ? 'Copiado!' : 'Copiar respostas'}
            </button>
            <button type="button" onClick={imputar} disabled={!podeEnviar}
              title={dados.carefy.erro ? 'Paciente não encontrado no Carefy' : vazios.length ? 'Preencha os campos obrigatórios' : enviado ? 'Já enviado' : 'Gravar este formulário no Carefy'}
              style={{ border: 0, background: podeEnviar ? P.primary : '#c7d2fe', color: '#fff', borderRadius: 8, padding: '9px 16px', fontWeight: 800, fontSize: '0.85rem', cursor: podeEnviar ? 'pointer' : 'not-allowed', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <Send size={15} /> {enviando ? 'Enviando…' : enviado ? 'Enviado' : 'Imputar agora'}
            </button>
          </div>

          {enviado && (
            <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 10, padding: '12px 16px', fontSize: '0.88rem', fontWeight: 700 }}>
              ✓ {enviado.formulario} de {enviado.paciente} gravado no Carefy (internação desde {fmtData(enviado.internacao?.desde)}). Confira em Formulários, no Carefy.
            </div>
          )}

          <details style={caixa}>
            <summary style={{ cursor: 'pointer', fontWeight: 700, color: P.text, fontSize: '0.85rem' }}>Texto completo do relatório do IW</summary>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', color: '#334155', marginTop: 10, fontFamily: 'inherit' }}>{dados.textoIW}</pre>
          </details>
        </>
      )}

      </>}

      {aba === 'enviar' && historico.length > 0 && (
        <div style={caixa}>
          <div style={{ fontWeight: 800, color: P.text, fontSize: '0.9rem', marginBottom: 8 }}>Últimos envios</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead><tr>{['Quando', 'Paciente', 'Formulário', 'Por', 'Resultado'].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: P.muted, fontSize: '0.7rem', textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
              <tbody>
                {historico.map(h => (
                  <tr key={h.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{new Date(h.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 700 }}>{h.paciente}</td>
                    <td style={{ padding: '6px 8px' }}>{h.formulario}</td>
                    <td style={{ padding: '6px 8px', color: P.muted }}>{h.quem}</td>
                    <td style={{ padding: '6px 8px' }}>{h.ok === 'true' ? <span style={chip('#dcfce7', '#166534')}>enviado</span> : <span style={chip('#fee2e2', '#991b1b')} title={h.erro || ''}>não enviado</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
